import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../services/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area,
  ComposedChart, Line,
} from 'recharts';
import './HROverview.css';

const DEPT_COLORS = {
  'Sales':            '#3b82f6',
  'Accounts':         '#10b981',
  'Tech / Marketing': '#8b5cf6',
  'Warehouse':        '#f59e0b',
  'Unassigned':       '#94a3b8',
  'Driver':           '#06b6d4',
  'Incharge':         '#f97316',
};

const CITY_CONFIG = {
  Lahore:    { color: '#3b82f6', gradient: 'linear-gradient(135deg, #1e3a8a, #3b82f6)', icon: '🏛️' },
  Karachi:   { color: '#10b981', gradient: 'linear-gradient(135deg, #065f46, #10b981)', icon: '🌊' },
  Islamabad: { color: '#8b5cf6', gradient: 'linear-gradient(135deg, #4c1d95, #8b5cf6)', icon: '🏔️' },
  Multan:    { color: '#f97316', gradient: 'linear-gradient(135deg, #9a3412, #f97316)', icon: '🕌' },
};

function getInitials(name) {
  if (!name) return '??';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[p.length - 1][0] || '')).toUpperCase();
}

function getAvatarClass(name) {
  const c = ['ov-av-blue','ov-av-teal','ov-av-amber','ov-av-purple','ov-av-coral','ov-av-green'];
  let h = 0;
  for (let i = 0; i < (name||'').length; i++) h += name.charCodeAt(i);
  return c[h % c.length];
}

function formatPKR(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1000000) return `PKR ${(n/1000000).toFixed(1)}M`;
  if (n >= 1000) return `PKR ${(n/1000).toFixed(0)}K`;
  return `PKR ${Number(n).toLocaleString('en-PK')}`;
}

function formatTime() {
  return new Date().toLocaleTimeString('en-PK', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
  });
}

function formatShortDate(d) {
  return new Date(d).toLocaleDateString('en-PK', { weekday: 'short', day: 'numeric' });
}

function isToday(dateStr) {
  const today = new Date().toLocaleDateString('en-CA');
  return dateStr === today;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const onTime  = payload.find(p => p.dataKey === 'OnTime')?.value ?? 0;
  const late    = payload.find(p => p.dataKey === 'Late')?.value ?? 0;
  const present = onTime + late;
  const rate    = payload.find(p => p.dataKey === 'Rate')?.value ?? 0;
  return (
    <div className="ov-tooltip-v2">
      <div className="ov-tooltip-v2-date">
        {formatShortDate(label)}
        {isToday(label) && <span className="ov-tooltip-today-tag">Today</span>}
      </div>
      <div className="ov-tooltip-v2-rate">{rate}% <span>attendance</span></div>
      <div className="ov-tooltip-v2-row">
        <span className="ov-tt-dot" style={{ background: '#6366f1' }} />
        <span className="ov-tt-label">Present</span>
        <span className="ov-tt-val">{present}</span>
      </div>
      <div className="ov-tooltip-v2-row">
        <span className="ov-tt-dot" style={{ background: '#f59e0b' }} />
        <span className="ov-tt-label">Late (of which)</span>
        <span className="ov-tt-val">{late}</span>
      </div>
    </div>
  );
};

// ── AI REPORT COMPONENT ───────────────────────────────────────────────────────

function AIReport({ data, onClose }) {
  const [report, setReport]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const reportRef               = useRef('');

  useEffect(() => {
    generateReport();
  }, []);

  const generateReport = async () => {
    try {
      setLoading(true);
      setReport('');
      reportRef.current = '';

      const now   = new Date();
      const month = now.toLocaleString('default', { month: 'long' });
      const year  = now.getFullYear();

      const prompt = `You are an HR analytics AI for Fotile Pakistan. Analyze the following attendance and salary data for ${month} ${year} and generate a comprehensive executive summary for the CEO/Boss.

DATA:
- Total Employees: ${data.totalEmployees}
- Currently Clocked In Today: ${data.clockedIn}
- Absent Today: ${data.absentToday}
- Late Today: ${data.lateToday}
- Attendance Rate Today: ${data.attendanceRate}%
- Total Net Payroll This Month: PKR ${Number(data.totalNetSalary).toLocaleString()}
- Total Salary Deductions This Month: PKR ${Number(data.totalDeductions).toLocaleString()}

DEPARTMENT BREAKDOWN:
${data.departments.map(d => `- ${d.department}: ${d.total} employees, ${d.present_today} present today, ${d.late_today} late today`).join('\n')}

EMPLOYEES WITH 3+ LATES THIS MONTH (at risk of deduction):
${data.lateAlerts.length > 0 ? data.lateAlerts.map(e => `- ${e.full_name} (${e.employee_id}): ${e.late_count} lates, ${e.city}, ${e.department || 'Unassigned'}`).join('\n') : 'None'}

EMPLOYEES ABSENT TODAY:
${data.absentList.slice(0, 10).map(e => `- ${e.full_name} (${e.employee_id}): ${e.department || 'Unassigned'}, ${e.city}`).join('\n')}

SALARY REPORTS (top deductions):
${data.salaryReports.slice(0, 5).map(r => `- ${r.employee_name} (${r.emp_code}): Base PKR ${Number(r.base_salary).toLocaleString()}, Deduction PKR ${Number(r.total_deduction).toLocaleString()} (${r.deduction_percent}%), Net PKR ${Number(r.net_salary).toLocaleString()}`).join('\n')}

14-DAY ATTENDANCE TREND:
${data.trend.slice(-7).map(t => `- ${t.date}: ${t.present_count} present, ${t.late_count} late, ${t.attendance_rate}% rate`).join('\n')}

Generate a professional executive summary with these sections:
1. **Overall Health Score** — rate the company attendance health as Excellent/Good/Needs Attention/Critical with a brief reason
2. **Today's Snapshot** — what's happening today
3. **Department Performance** — which department is doing best and worst, with specific observations
4. **Employees Requiring Attention** — name specific employees who are consistently late or absent, be direct
5. **Financial Impact** — total deductions, who is costing the most in deductions
6. **Recommendations** — 2-3 specific actionable recommendations for management

Be direct, specific, use employee names, use PKR amounts. Write in a professional but readable tone. Use emojis for section headers. Keep it comprehensive but scannable.`;

      const response = await fetch('https://fotileams.com/api/ai/summary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();
      const text   = result.report || 'Failed to generate report.';
      setReport(text);
    } catch (err) {
      setError('Failed to generate AI report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatReport = (text) => {
    return text.split('\n').map((line, i) => {
      const trimmed = line.trim();

      if (/^# /.test(trimmed)) {
        return <h2 key={i} className="ai-h1">{trimmed.replace(/^# /, '')}</h2>;
      }
      if (/^## /.test(trimmed)) {
        return <h3 key={i} className="ai-section-title">{trimmed.replace(/^## /, '')}</h3>;
      }
      if (/^### /.test(trimmed)) {
        return <h4 key={i} className="ai-h3">{trimmed.replace(/^### /, '')}</h4>;
      }
      if (/^---+$/.test(trimmed)) {
        return <hr key={i} className="ai-divider" />;
      }
      if (/^[-•*] /.test(trimmed)) {
        const inner = trimmed.replace(/^[-•*] /, '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        return <li key={i} className="ai-li" dangerouslySetInnerHTML={{ __html: inner }} />;
      }
      if (/^\d+\./.test(trimmed)) {
        const inner = trimmed.replace(/^\d+\.\s*/, '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        return <li key={i} className="ai-li ai-li-num" dangerouslySetInnerHTML={{ __html: inner }} />;
      }
      if (trimmed === '') return <div key={i} className="ai-spacer" />;
      const html = trimmed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return <p key={i} className="ai-para" dangerouslySetInnerHTML={{ __html: html }} />;
    });
  };

  return (
    <div className="ai-overlay" onClick={onClose}>
      <div className="ai-modal" onClick={e => e.stopPropagation()}>
        <div className="ai-modal-header">
          <div className="ai-modal-title-row">
            <div className="ai-icon">✦</div>
            <div>
              <div className="ai-modal-title">AI Executive Summary</div>
              <div className="ai-modal-sub">
                {new Date().toLocaleDateString('en-PK', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
            </div>
          </div>
          <div className="ai-modal-actions">
            <button className="ai-regen-btn" onClick={generateReport} disabled={loading}>
              {loading ? '…' : '↻ Regenerate'}
            </button>
            <button className="ai-close-btn" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="ai-modal-body">
          {loading ? (
            <div className="ai-loading">
              <div className="ai-loading-dots">
                <span /><span /><span />
              </div>
              <p>Claude is analyzing your attendance data…</p>
            </div>
          ) : error ? (
            <div className="ai-error">{error}</div>
          ) : (
            <div className="ai-report-content">
              {formatReport(report)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── MAIN OVERVIEW ─────────────────────────────────────────────────────────────

export default function HROverview() {
  const [time, setTime]             = useState(formatTime());
  const [todayData, setTodayData]   = useState(null);
  const [trend, setTrend]           = useState([]);
  const [departments, setDepartments] = useState([]);
  const [alerts, setAlerts]         = useState(null);
  const [employees, setEmployees]   = useState([]);
  const [salaryData, setSalaryData] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [showAI, setShowAI]         = useState(false);

  useEffect(() => {
    const t = setInterval(() => setTime(formatTime()), 1000);
    return () => clearInterval(t);
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const now   = new Date();
      const month = now.getMonth() + 1;
      const year  = now.getFullYear();
      const [todayRes, trendRes, deptRes, alertsRes, empRes, salRes] = await Promise.all([
        api.getAllAttendance(),
        api.getAttendanceTrend(14),
        api.getDepartmentStats(),
        api.getAlerts(),
        api.getAllEmployees(),
        api.getAllSalaryReports({ month, year }),
      ]);
      setTodayData(todayRes);
      setTrend(trendRes.trend || []);
      setDepartments(deptRes.departments || []);
      setAlerts(alertsRes);
      setEmployees(empRes.employees || []);
      setSalaryData(salRes);
      setLastRefresh(new Date());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 120000);
    return () => clearInterval(t);
  }, [fetchAll]);

  const totalEmployees = employees.length;
  const clockedIn      = todayData?.summary?.clocked_in  || 0;
  const clockedOut     = todayData?.summary?.clocked_out || 0;
  const lateToday      = todayData?.summary?.late        || 0;
  const totalPresent   = todayData?.summary?.total       || 0;
  const absentToday    = Math.max(0, totalEmployees - totalPresent);
  const attendanceRate = totalEmployees > 0 ? Math.round((totalPresent / totalEmployees) * 100) : 0;
  const totalDeductions = parseFloat(salaryData?.summary?.total_deductions || 0);
  const totalNetSalary  = parseFloat(salaryData?.summary?.total_net_salary  || 0);
  const lateAlertCount  = alerts?.late_alerts?.length || 0;

  const cityStats = ['Lahore','Karachi','Islamabad','Multan'].map(city => {
    const cityEmps    = employees.filter(e => e.city === city);
    const cityPresent = (todayData?.records || []).filter(r => r.city === city).length;
    return { city, total: cityEmps.length, present: cityPresent };
  });

  const trendData = trend.slice(-14).map(t => ({
    date: t.date,
    OnTime: Math.max(0, t.present_count - t.late_count),
    Late: t.late_count,
    Present: t.present_count,
    Rate: t.attendance_rate,
  }));

  // ── Trend insight stats ──
  const nonZeroTrend = trendData.filter(t => t.Present > 0 || t.Late > 0);
  const avgPresent = nonZeroTrend.length > 0
    ? Math.round(nonZeroTrend.reduce((s, t) => s + t.Present, 0) / nonZeroTrend.length)
    : 0;
  const avgRate = nonZeroTrend.length > 0
    ? Math.round(nonZeroTrend.reduce((s, t) => s + t.Rate, 0) / nonZeroTrend.length)
    : 0;
  const bestDay = nonZeroTrend.length > 0
    ? nonZeroTrend.reduce((best, t) => t.Present > best.Present ? t : best, nonZeroTrend[0])
    : null;
  const last3 = nonZeroTrend.slice(-3);
  const prev3 = nonZeroTrend.slice(-6, -3);
  const last3Avg = last3.length > 0 ? last3.reduce((s,t)=>s+t.Present,0) / last3.length : 0;
  const prev3Avg = prev3.length > 0 ? prev3.reduce((s,t)=>s+t.Present,0) / prev3.length : 0;
  const trendDirection = prev3.length > 0 ? (last3Avg >= prev3Avg ? 'up' : 'down') : null;
  const trendPct = prev3Avg > 0 ? Math.round(((last3Avg - prev3Avg) / prev3Avg) * 100) : 0;

  const now = new Date();

  const aiData = {
    totalEmployees, clockedIn, absentToday, lateToday, attendanceRate,
    totalNetSalary, totalDeductions,
    departments,
    lateAlerts:    alerts?.late_alerts || [],
    absentList:    alerts?.absent_today || [],
    salaryReports: salaryData?.reports || [],
    trend,
  };

  if (loading) {
    return (
      <div className="ov-loading">
        <div className="ov-spinner" />
        <p>Loading mission control…</p>
      </div>
    );
  }

  return (
    <div className="ov-root">

      {showAI && <AIReport data={aiData} onClose={() => setShowAI(false)} />}

      <div className="ov-topbar">
        <div className="ov-topbar-left">
          <div className="ov-title-row">
            <h1 className="ov-title">Mission Control</h1>
            <span className="ov-live-badge">● LIVE</span>
          </div>
          <span className="ov-date">
            {now.toLocaleDateString('en-PK', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
        </div>
        <div className="ov-topbar-right">
          <div className="ov-clock-box">
            <span className="ov-clock">{time}</span>
            <span className="ov-tz">PKT</span>
          </div>
          {lastRefresh && (
            <span className="ov-refresh-time">
              Refreshed {lastRefresh.toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit', hour12: true })}
            </span>
          )}
          <button className="ov-ai-btn" onClick={() => setShowAI(true)}>
            ✦ AI Summary
          </button>
          <button className="ov-refresh-btn" onClick={fetchAll}>↻ Refresh</button>
        </div>
      </div>

      {lateAlertCount > 0 && (
        <div className="ov-alert-banner">
          <span className="ov-alert-icon">⚠</span>
          <span>{lateAlertCount} employee{lateAlertCount > 1 ? 's' : ''} have reached 3+ late arrivals this month — salary deduction will apply</span>
          <span className="ov-alert-action" onClick={() => setShowAI(true)}>View AI Analysis →</span>
        </div>
      )}

      <div className="ov-main-grid">
        <div className="ov-left-col">

          <div className="ov-kpi-strip">

            {/* Total Employees */}
            <div className="ov-kpi-v2 ov-kpi-v2-navy">
              <div className="ov-kpi-v2-watermark">👥</div>
              <div className="ov-kpi-v2-label">Total Employees</div>
              <div className="ov-kpi-v2-num">{totalEmployees}</div>
              <div className="ov-kpi-v2-sub">Active workforce</div>
              <div className="ov-kpi-v2-footer">
                <div className="ov-kpi-v2-bar-track">
                  <div className="ov-kpi-v2-bar-fill" style={{ width: '100%' }} />
                </div>
              </div>
            </div>

            {/* Currently In */}
            <div className="ov-kpi-v2 ov-kpi-v2-emerald">
              <div className="ov-kpi-v2-watermark">⚡</div>
              <div className="ov-kpi-v2-label">
                <span className="ov-kpi-v2-live-dot" />
                Currently In
              </div>
              <div className="ov-kpi-v2-num">{clockedIn}</div>
              <div className="ov-kpi-v2-sub">{clockedOut} clocked out</div>
              <div className="ov-kpi-v2-footer">
                <div className="ov-kpi-v2-bar-track">
                  <div className="ov-kpi-v2-bar-fill"
                    style={{ width: `${totalEmployees > 0 ? Math.round((clockedIn / totalEmployees) * 100) : 0}%` }} />
                </div>
                <span className="ov-kpi-v2-pct">
                  {totalEmployees > 0 ? Math.round((clockedIn / totalEmployees) * 100) : 0}%
                </span>
              </div>
            </div>

            {/* Absent Today */}
            <div className="ov-kpi-v2 ov-kpi-v2-rose">
              <div className="ov-kpi-v2-watermark">✗</div>
              <div className="ov-kpi-v2-label">Absent Today</div>
              <div className="ov-kpi-v2-num">{absentToday}</div>
              <div className="ov-kpi-v2-sub">Not clocked in</div>
              <div className="ov-kpi-v2-footer">
                <div className="ov-kpi-v2-bar-track">
                  <div className="ov-kpi-v2-bar-fill"
                    style={{ width: `${totalEmployees > 0 ? Math.round((absentToday / totalEmployees) * 100) : 0}%` }} />
                </div>
                <span className="ov-kpi-v2-pct">
                  {totalEmployees > 0 ? Math.round((absentToday / totalEmployees) * 100) : 0}%
                </span>
              </div>
            </div>

            {/* Late Today */}
            <div className="ov-kpi-v2 ov-kpi-v2-amber">
              <div className="ov-kpi-v2-watermark">⏰</div>
              <div className="ov-kpi-v2-label">Late Today</div>
              <div className="ov-kpi-v2-num">{lateToday}</div>
              <div className="ov-kpi-v2-sub">Past grace period</div>
              <div className="ov-kpi-v2-footer">
                <div className="ov-kpi-v2-bar-track">
                  <div className="ov-kpi-v2-bar-fill"
                    style={{ width: `${totalPresent > 0 ? Math.round((lateToday / totalPresent) * 100) : 0}%` }} />
                </div>
                <span className="ov-kpi-v2-pct">
                  {totalPresent > 0 ? Math.round((lateToday / totalPresent) * 100) : 0}% of present
                </span>
              </div>
            </div>

            {/* Net Payroll */}
            <div className="ov-kpi-v2 ov-kpi-v2-purple">
              <div className="ov-kpi-v2-watermark">₨</div>
              <div className="ov-kpi-v2-label">Net Payroll</div>
              <div className="ov-kpi-v2-num ov-kpi-v2-num-sm">{formatPKR(totalNetSalary)}</div>
              <div className="ov-kpi-v2-sub">{formatPKR(totalDeductions)} deducted</div>
              <div className="ov-kpi-v2-footer">
                <div className="ov-kpi-v2-bar-track">
                  <div className="ov-kpi-v2-bar-fill"
                    style={{ width: totalNetSalary > 0 ? `${Math.round((totalNetSalary / (totalNetSalary + totalDeductions)) * 100)}%` : '100%' }} />
                </div>
              </div>
            </div>

          </div>

          {/* TREND CHART — V2 REDESIGN */}
          <div className="ov-card ov-trend-card-v2">
            <div className="ov-card-header">
              <div>
                <div className="ov-card-title">Attendance Trend</div>
                <div className="ov-card-sub">Last 14 days · present count, late arrivals & rate</div>
              </div>
              {trendDirection && (
                <div className={`ov-trend-pill ov-trend-${trendDirection}`}>
                  {trendDirection === 'up' ? '↗' : '↘'} {Math.abs(trendPct)}% vs prev 3 days
                </div>
              )}
            </div>

            <div className="ov-insight-strip">
              <div className="ov-insight-item">
                <span className="ov-insight-num">{avgPresent}</span>
                <span className="ov-insight-lbl">Avg Present</span>
              </div>
              <div className="ov-insight-divider" />
              <div className="ov-insight-item">
                <span className="ov-insight-num">{avgRate}%</span>
                <span className="ov-insight-lbl">Avg Rate</span>
              </div>
              <div className="ov-insight-divider" />
              <div className="ov-insight-item">
                <span className="ov-insight-num">{bestDay ? bestDay.Present : '—'}</span>
                <span className="ov-insight-lbl">Best Day{bestDay ? ` · ${formatShortDate(bestDay.date)}` : ''}</span>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={230}>
              <ComposedChart data={trendData} margin={{ top: 10, right: 10, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="presentBarGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#818cf8" stopOpacity={1}/>
                    <stop offset="100%" stopColor="#6366f1" stopOpacity={0.75}/>
                  </linearGradient>
                  <linearGradient id="todayBarGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#fb7185" stopOpacity={1}/>
                    <stop offset="100%" stopColor="#e31837" stopOpacity={0.9}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="date" tickFormatter={formatShortDate}
                  tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="right" orientation="right" domain={[0, 100]}
                  tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                  tickFormatter={(v) => `${v}%`} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(99,102,241,0.06)' }} />
                <Bar yAxisId="left" dataKey="OnTime" stackId="att" maxBarSize={28}>
                  {trendData.map((entry, i) => (
                    <Cell key={i} fill={isToday(entry.date) ? 'url(#todayBarGrad)' : 'url(#presentBarGrad)'} />
                  ))}
                </Bar>
                <Bar yAxisId="left" dataKey="Late" stackId="att" radius={[6, 6, 0, 0]} maxBarSize={28}>
                  {trendData.map((entry, i) => (
                    <Cell key={i} fill={isToday(entry.date) ? '#fde047' : '#f59e0b'} />
                  ))}
                </Bar>
                <Line yAxisId="right" type="monotone" dataKey="Rate" stroke="#f59e0b" strokeWidth={2.5}
                  dot={{ r: 3, fill: '#f59e0b', strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: '#f59e0b' }} />
              </ComposedChart>
            </ResponsiveContainer>

            <div className="ov-chart-legend">
              <span className="ov-legend-item"><span className="ov-legend-swatch" style={{ background: 'linear-gradient(180deg,#818cf8,#6366f1)' }} />On Time</span>
              <span className="ov-legend-item"><span className="ov-legend-swatch" style={{ background: '#f59e0b' }} />Late</span>
              <span className="ov-legend-item"><span className="ov-legend-swatch" style={{ background: 'linear-gradient(180deg,#fb7185,#e31837)' }} />Today</span>
              <span className="ov-legend-item"><span className="ov-legend-line" />Attendance Rate</span>
            </div>
          </div>

          <div className="ov-city-grid">
            {cityStats.map(({ city, total, present }) => {
              const cfg  = CITY_CONFIG[city] || { color: '#94a3b8', gradient: 'linear-gradient(135deg, #475569, #94a3b8)', icon: '📍' };
              const rate = total > 0 ? Math.round((present / total) * 100) : 0;
              const isEmpty = total === 0;
              return (
                <div key={city} className="ov-city-v2" style={{ background: cfg.gradient }}>
                  <div className="ov-city-v2-watermark">{cfg.icon}</div>
                  <div className="ov-city-v2-name">{city}</div>
                  {isEmpty ? (
                    <>
                      <div className="ov-city-v2-empty">No employees yet</div>
                      <div className="ov-city-v2-footer">
                        <div className="ov-city-v2-bar-track"><div className="ov-city-v2-bar-fill" style={{ width: '0%' }} /></div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="ov-city-v2-num">{present}<span>/{total}</span></div>
                      <div className="ov-city-v2-footer">
                        <div className="ov-city-v2-bar-track">
                          <div className="ov-city-v2-bar-fill" style={{ width: `${rate}%` }} />
                        </div>
                        <span className="ov-city-v2-pct">{rate}%</span>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Top Salary Impact — fills the space below city grid */}
          <div className="ov-card">
            <div className="ov-card-header">
              <div>
                <div className="ov-card-title">💰 Top Salary Impact This Month</div>
                <div className="ov-card-sub">Employees with the highest deductions</div>
              </div>
            </div>
            {(!salaryData?.reports || salaryData.reports.length === 0) ? (
              <div className="ov-no-alerts">✓ No deductions recorded this month</div>
            ) : (
              <div className="ov-salary-impact-list">
                {[...salaryData.reports]
                  .filter(r => parseFloat(r.total_deduction) > 0)
                  .sort((a, b) => parseFloat(b.total_deduction) - parseFloat(a.total_deduction))
                  .slice(0, 5)
                  .map((r, i) => {
                    const pct = Math.min(100, parseFloat(r.deduction_percent) || 0);
                    const barColor = pct >= 15 ? '#ef4444' : pct >= 7 ? '#f59e0b' : '#3b82f6';
                    return (
                      <div key={i} className="ov-salary-impact-row">
                        <div className={`ov-salary-impact-av ${getAvatarClass(r.employee_name)}`}>
                          {getInitials(r.employee_name)}
                        </div>
                        <div className="ov-salary-impact-info">
                          <div className="ov-salary-impact-name-row">
                            <span className="ov-salary-impact-name">{r.employee_name}</span>
                            <span className="ov-salary-impact-amount">
                              -{formatPKR(r.total_deduction)}
                            </span>
                          </div>
                          <div className="ov-salary-impact-bar-track">
                            <div className="ov-salary-impact-bar-fill" style={{ width: `${pct}%`, background: barColor }} />
                          </div>
                        </div>
                        <span className="ov-salary-impact-pct" style={{ color: barColor }}>
                          {r.deduction_percent}%
                        </span>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>

        <div className="ov-right-col">

          <div className="ov-card ov-gauge-card">
            <div className="ov-card-title" style={{ marginBottom: 8 }}>Today's Rate</div>
            <div className="ov-gauge-wrap">
              <svg width="140" height="140" viewBox="0 0 140 140">
                <circle cx="70" cy="70" r="54" fill="none" stroke="#f1f5f9" strokeWidth="12" />
                <circle cx="70" cy="70" r="54" fill="none"
                  stroke={attendanceRate >= 80 ? '#10b981' : attendanceRate >= 60 ? '#f59e0b' : '#ef4444'}
                  strokeWidth="12"
                  strokeDasharray={`${2 * Math.PI * 54}`}
                  strokeDashoffset={`${2 * Math.PI * 54 * (1 - attendanceRate / 100)}`}
                  strokeLinecap="round"
                  transform="rotate(-90 70 70)"
                  style={{ transition: 'stroke-dashoffset 1s ease' }}
                />
                <text x="70" y="64" textAnchor="middle" fontSize="26" fontWeight="700" fill="#0f172a">
                  {attendanceRate}%
                </text>
                <text x="70" y="82" textAnchor="middle" fontSize="11" fill="#94a3b8">
                  Attendance
                </text>
              </svg>
            </div>
            <div className="ov-gauge-stats">
              <div className="ov-gauge-stat">
                <span className="ov-gauge-num ov-green">{totalPresent}</span>
                <span className="ov-gauge-lbl">Present</span>
              </div>
              <div className="ov-gauge-divider" />
              <div className="ov-gauge-stat">
                <span className="ov-gauge-num ov-red">{absentToday}</span>
                <span className="ov-gauge-lbl">Absent</span>
              </div>
            </div>
          </div>

          <div className="ov-card">
            <div className="ov-dept-v2-header">
              <div className="ov-card-title">By Department</div>
              <span className="ov-dept-v2-total">{departments.filter(d=>parseInt(d.total)>0).length} depts</span>
            </div>
            <div className="ov-dept-v2-list">
              {departments.filter(d => parseInt(d.total) > 0)
                .sort((a,b) => {
                  const rateA = parseInt(a.total) > 0 ? parseInt(a.present_today)/parseInt(a.total) : 0;
                  const rateB = parseInt(b.total) > 0 ? parseInt(b.present_today)/parseInt(b.total) : 0;
                  return rateB - rateA;
                })
                .map((d, i, arr) => {
                  const color     = DEPT_COLORS[d.department] || '#94a3b8';
                  const total     = parseInt(d.total);
                  const present   = parseInt(d.present_today);
                  const late      = parseInt(d.late_today) || 0;
                  const rate      = total > 0 ? Math.round((present / total) * 100) : 0;
                  const rateColor = rate >= 80 ? '#10b981' : rate >= 60 ? '#f59e0b' : '#ef4444';
                  const isFirst   = i === 0 && total > 1;
                  const isLast    = i === arr.length - 1 && total > 1 && rate < 80;
                  return (
                    <div key={i} className="ov-dept-v3-row">
                      {/* Background fill */}
                      <div className="ov-dept-v3-fill"
                        style={{ width: `${rate}%`, background: rateColor }} />
                      {/* Left — color dot + name */}
                      <div className="ov-dept-v3-left">
                        <span className="ov-dept-v3-dot" style={{ background: color }} />
                        <span className="ov-dept-v3-name">{d.department || 'Unassigned'}</span>
                        {late > 0 && <span className="ov-dept-v3-late">⏱ {late}</span>}
                        {isFirst && <span className="ov-dept-v3-badge ov-dept-v3-best">★ Best</span>}
                        {isLast  && <span className="ov-dept-v3-badge ov-dept-v3-low">↑ Needs work</span>}
                      </div>
                      {/* Right — count + pct */}
                      <div className="ov-dept-v3-right">
                        <span className="ov-dept-v3-count">{present}/{total}</span>
                        <span className="ov-dept-v3-pct" style={{ color: rateColor }}>{rate}%</span>
                      </div>
                    </div>
                  );
              })}
            </div>
          </div>

          <div className="ov-card ov-alerts-card">
            <div className="ov-card-title" style={{ marginBottom: 10 }}>
              Alerts
              {lateAlertCount > 0 && (
                <span className="ov-alert-count">{lateAlertCount}</span>
              )}
            </div>
            {lateAlertCount === 0 && !alerts?.consecutive_absent?.length ? (
              <div className="ov-no-alerts">✓ All clear — no alerts today</div>
            ) : (
              <div className="ov-alerts-list">
                {alerts?.late_alerts?.slice(0, 4).map((emp, i) => (
                  <div key={i} className="ov-alert-row ov-alert-amber">
                    <div className={`ov-alert-av ${getAvatarClass(emp.full_name)}`}>
                      {getInitials(emp.full_name)}
                    </div>
                    <div className="ov-alert-info">
                      <span className="ov-alert-name">{emp.full_name}</span>
                      <span className="ov-alert-detail">{emp.late_count} lates · {emp.city}</span>
                    </div>
                    <span className="ov-alert-tag ov-tag-amber">
                      {emp.late_count >= 6 ? 'Full day' : 'Half day'}
                    </span>
                  </div>
                ))}
                {alerts?.consecutive_absent?.slice(0, 3).map((emp, i) => (
                  <div key={i} className="ov-alert-row ov-alert-red">
                    <div className={`ov-alert-av ${getAvatarClass(emp.full_name)}`}>
                      {getInitials(emp.full_name)}
                    </div>
                    <div className="ov-alert-info">
                      <span className="ov-alert-name">{emp.full_name}</span>
                      <span className="ov-alert-detail">{emp.absent_days} absences · {emp.city}</span>
                    </div>
                    <span className="ov-alert-tag ov-tag-red">Absent</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {alerts?.absent_today?.length > 0 && (
        <div className="ov-card ov-absent-section">
          <div className="ov-card-header">
            <div>
              <div className="ov-card-title">Not Clocked In Today</div>
              <div className="ov-card-sub">{alerts.absent_today.length} employees haven't clocked in</div>
            </div>
          </div>
          <div className="ov-absent-grid">
            {alerts.absent_today.map((emp, i) => (
              <div key={i} className="ov-absent-card">
                <div className={`ov-absent-av ${getAvatarClass(emp.full_name)}`}>
                  {getInitials(emp.full_name)}
                </div>
                <div className="ov-absent-info">
                  <span className="ov-absent-name">{emp.full_name}</span>
                  <span className="ov-absent-meta">
                    {emp.employee_id} · {emp.department || '—'} · {emp.city}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}